import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, fetchUserAttributes, type AuthUser } from 'aws-amplify/auth';

interface User {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  classYear: number;
  position?: string;
  jerseyNumber?: number;
  isAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const authUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const userData = parseUserFromAttributes(authUser, attributes);
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const parseUserFromAttributes = (authUser: AuthUser, attributes: any): User => {
    return {
      username: authUser.username,
      email: attributes.email || '',
      firstName: attributes.given_name || '',
      lastName: attributes.family_name || '',
      classYear: parseInt(attributes['custom:class_year'] || '0'),
      position: attributes['custom:position'],
      jerseyNumber: attributes['custom:jersey_number'] ? parseInt(attributes['custom:jersey_number']) : undefined,
      isAdmin: attributes['custom:is_admin'] === 'true',
    };
  };

  const login = async (username: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      await signIn({ username, password });
      // After successful sign in, get user and attributes
      const authUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const userData = parseUserFromAttributes(authUser, attributes);
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const authUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const userData = parseUserFromAttributes(authUser, attributes);
      setUser(userData);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      loading,
      login,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
